// Boundary validation for the two PII-safe farmer WFS layers (CLAUDE.md §8).
// Every response from geonode.fairchain.org is untrusted until it passes one of
// these schemas. A field that fails validation must surface an explicit
// unavailable state upstream - never a silent zero, NaN, or half-populated card.
import { z } from 'zod';

// GeoServer returns a GeoJSON Point centroid for both layers. Coordinates may be
// absent for a cohort with no mappable centroid (e.g. Uganda has a card but no
// pins yet) - geometry is therefore nullable and consumers must null-guard it.
const pointGeometrySchema = z
  .object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  })
  .nullable();

// geonode:farmer_cards_public - 5 cohort summary rows.
// farmer_count / as_at_year are coerced: WFS sometimes serialises numerics as
// strings. farmer_count MUST be a finite number (it feeds the 13,391 headline);
// a non-numeric value fails validation rather than rendering as NaN.
const farmerCardPropertiesSchema = z.object({
  cohort: z.string(),
  farmer_count: z.coerce.number().finite(),
  as_at_year: z.coerce.string(),
  title: z.string(),
  location_label: z.string(),
  card_image: z.string().nullable().optional(),
  intro_text: z.string().nullable().optional(),
});

const farmerCardFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.union([z.string(), z.number()]).optional(),
  properties: farmerCardPropertiesSchema,
  geometry: pointGeometrySchema,
});

export const farmerCardsCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(farmerCardFeatureSchema),
});

// geonode:dashboard_combined_public - PII-stripped pin layer. z.object() strips
// any key not declared here, so a field the FarmerModal reads but that is NOT
// listed below never reaches the component: it arrives `undefined`, and
// parseFloat(undefined) → NaN. That is exactly why the popup showed
// "undefined NaN" for price - lirp_price et al. were being served by the layer
// but silently dropped at this boundary. Fields below are the display set the
// modal renders. Phone/email/income remain undeclared and thus stripped.
//
// REVIEW-REQUIRED (§11): lirp_price / moyee_price are farmer price fields shown
// on the public site. They come from the designated _public layer and restore
// an existing modal display (not a new field), but they touch farmer financial
// data - flag for the founder's sign-off before production deploy.
//
// WFS serialises numerics inconsistently (sometimes as JSON strings, e.g.
// lirp_price "78.12"), so the numeric-ish fields accept string | number and the
// component coerces at read time. Unknown/absent values stay nullable+optional
// so a partial row degrades to hidden lines, never a NaN or a false zero (§10).
const numericLike = z.union([z.number(), z.string()]).nullable().optional();

const safePinPropertiesSchema = z.object({
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  profile_photo: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  farmer_group: z.string().nullable().optional(),
  // registration_date is in the underlying DB view but not yet exposed by the WFS
  // layer (needs a GeoServer feature-type reload). Declared here so the "Since"
  // line lights up automatically once WFS returns it - no further code change.
  // Not sensitive PII (programme join date), so safe to display.
  registration_date: z.string().nullable().optional(),
  // Farm + price display fields (dashboard_combined_public). Previously stripped
  // here, which blanked the FarmerModal price bar and farm-info rows.
  lirp_price: numericLike,
  moyee_price: numericLike,
  currency: z.string().nullable().optional(),
  coffee_variety: z.string().nullable().optional(),
  plots_number: numericLike,
  plot_size: numericLike,
  trainings: numericLike,
});

const safePinFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.union([z.string(), z.number()]).optional(),
  properties: safePinPropertiesSchema,
  geometry: pointGeometrySchema,
});

export const safePinCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(safePinFeatureSchema),
});

// ---------------------------------------------------------------------------
// D16a - forest outline layers (polygon geometry).
// Three layers share the rendering path: the two existing Mizan planting-zone
// layers (geonode:mizan1zones, geonode:mizan2_zone_data_public) and the
// computed geonode:tree_project_extents view. All are aggregate geometry -
// no per-farmer fields are declared, so anything else the layers carry is
// stripped at this boundary.
// ---------------------------------------------------------------------------

// A GeoJSON position. The Mizan zone layers serialise 3-element positions
// (x, y, elevation), so length is 2+ rather than exactly 2.
const positionSchema = z.array(z.number()).min(2);

const polygonGeometrySchema = z.union([
  z.object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(positionSchema).min(4)),
  }),
  z.object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(z.array(positionSchema).min(4))),
  }),
]);

// geonode:tree_project_extents - computed per-project concave hulls.
// tree_count is coerced (WFS sometimes serialises numerics as strings) and must
// be finite; a non-numeric value fails validation rather than rendering NaN.
const treeExtentFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.union([z.string(), z.number()]).optional(),
  properties: z.object({
    project: z.string(),
    tree_count: z.coerce.number().finite(),
  }),
  geometry: polygonGeometrySchema.nullable(),
});

export const treeExtentsCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(treeExtentFeatureSchema),
});

// Mizan planting-zone layers - only the geometry is rendered; no property is
// needed for display, so none is declared and all are stripped.
const planingZoneFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.union([z.string(), z.number()]).optional(),
  properties: z.object({}).nullable().optional(),
  geometry: polygonGeometrySchema.nullable(),
});

export const plantingZonesCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(planingZoneFeatureSchema),
});

export type TreeExtentsCollection = z.infer<typeof treeExtentsCollectionSchema>;
export type PlantingZonesCollection = z.infer<typeof plantingZonesCollectionSchema>;

// geonode:farmer_group_summary - one row per farmer project (Limu, Mizan, …).
//
// HONEST SCOPE NOTE (2026-09-05): this layer is still fetched through the generic
// fetchGeoServer path and is NOT validated at the boundary yet - its properties
// reach Project.tsx as `any`. This schema declares the fields the project page
// reads as content/tiles so they have a type, and it is exercised by tests, but
// wiring it into the fetch (fail-visible on a bad row) is a separate change
// recorded under Proposals in PROGRESS.md. Loose object: the layer also carries
// gallery/background_*_N fields the page reads dynamically, and Fairchain may
// add columns without notice.
//
// farmers_supplying: upstream is adding this column ("827" - farmers who
// delivered coffee in 2025). Nullable + optional so a row without it, or with
// the "na" placeholder, parses fine; the tile is hidden in both cases.
export const farmerGroupSummaryPropertiesSchema = z.looseObject({
  title: z.string().nullable().optional(),
  intro_text: z.string().nullable().optional(),
  background_intro: z.string().nullable().optional(),
  farmers: z.string().nullable().optional(),
  farmers_supplying: z.string().nullable().optional(),
});

// key_impact_data_live.labels_json - a JSON string of { key: value } carrying
// every label, caption and unit shown on the dashboard (founder decision
// 2026-09-06: no dashboard wording lives in code, and NO separate layer - the
// labels ride on the headline row this app already fetches). Keys look like
// "headline.farmers.label", "tile.jobs.label", "card.caption.limu". Validated
// after JSON.parse in parseLabelsJson(): anything that is not a flat
// string → string|null object is rejected whole and the built-in fallback text
// is used. A null value means "no label" (fallback wins), same as a blank one.
export const labelsJsonSchema = z.record(z.string(), z.string().nullable());

export type LabelsJson = z.infer<typeof labelsJsonSchema>;
export type FarmerGroupSummaryProperties = z.infer<typeof farmerGroupSummaryPropertiesSchema>;
export type FarmerCardsCollection = z.infer<typeof farmerCardsCollectionSchema>;
// ---------------------------------------------------------------------------
// geonode:all_trees_combined - photographed tree pins (founder decision
// 2026-09-18, Option 2). Fetched per project, so this schema validates ONE
// feature at a time: an invalid row is dropped and counted rather than
// failing the whole project (the founder asked for "drop and count").
// Limu/BAC rows arrive as Point; Mizan/Dark Roast rows arrive as MultiPoint
// (observed live 2026-09-18) - getTransformedData already collapses a
// MultiPoint to its first position, so both shapes are accepted here.
// Only the display set the tree popup reads is declared; farmer_id and every
// other per-farmer column are stripped at this boundary (§12).
// ---------------------------------------------------------------------------
const lngLatSchema = z.tuple([z.number().finite(), z.number().finite()]).rest(z.number());

const treePinGeometrySchema = z.union([
  z.object({ type: z.literal('Point'), coordinates: lngLatSchema }),
  z.object({ type: z.literal('MultiPoint'), coordinates: z.array(lngLatSchema).min(1) }),
]);

export const treePinFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.union([z.string(), z.number()]).optional(),
  geometry: treePinGeometrySchema,
  properties: z.object({
    fid: z.union([z.string(), z.number()]).nullable().optional(),
    photo: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    planting_date: z.string().nullable().optional(),
    farmer_group: z.string().nullable().optional(),
  }),
});

// The envelope only. Features are validated one by one by
// validateTreePinFeatures (src/api/trees.ts) so a single bad row cannot take
// the whole project down.
export const treePinEnvelopeSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(z.unknown()),
  numberMatched: z.coerce.number().finite().optional(),
  totalFeatures: z.coerce.number().finite().optional(),
  timeStamp: z.string().optional(),
  // GeoServer sends `"crs": null` for an EMPTY collection (captured live from
  // the Dark Roast request, 2026-09-18) - nullable, not just optional, or a
  // legitimately empty project is misreported as a failed one.
  crs: z
    .object({ type: z.string(), properties: z.object({ name: z.string() }) })
    .nullable()
    .optional(),
});

export type TreePinFeature = z.infer<typeof treePinFeatureSchema>;

export type FarmerCardFeature = z.infer<typeof farmerCardFeatureSchema>;
export type SafePinCollection = z.infer<typeof safePinCollectionSchema>;

/**
 * Validate a parsed WFS response against a schema. On failure, log enough to
 * diagnose from logs alone (which layer, which field, what shape arrived) and
 * throw so the caller renders an explicit unavailable state (§8, §10).
 */
export const validateLayer = <T>(layerName: string, schema: z.ZodType<T>, data: unknown): T => {
  const result = schema.safeParse(data);
  if (!result.success) {
    console.error(
      `[boundary] ${layerName}: validation failed`,
      result.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
    );
    throw new Error(`${layerName}: response failed schema validation`);
  }
  return result.data;
};
